'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

interface DeferredSectionProps {
  children: ReactNode;
  minHeight: number;
}

/** Mount expensive below-the-fold widgets shortly before they enter view. */
export default function DeferredSection({ children, minHeight }: DeferredSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || !('IntersectionObserver' in window)) {
      setShouldRender(true);
      return;
    }

    let observer: IntersectionObserver | undefined;
    // The catalog is copied from the server payload into context after
    // hydration. Wait for that layout before testing this section's position.
    const timer = window.setTimeout(() => {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShouldRender(true);
            observer?.disconnect();
          }
        },
        { rootMargin: '500px 0px' },
      );
      observer.observe(element);
    }, 1000);

    return () => {
      clearTimeout(timer);
      observer?.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} style={shouldRender ? undefined : { minHeight }}>
      {shouldRender ? children : null}
    </div>
  );
}
